import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from "yup";
import { Row, Col, Button, Form, CardHeader, CardBody, Card, CardFooter, Input } from 'reactstrap';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import { SludgeBilgeWaterMessages, commonValidationMessages } from 'Components/ValidationErrorMessages';
import apiGlobal from 'global/api.global';
import { useQuery } from 'react-query';
import { queryKeyes } from 'shared/queryKeys';
import { Get } from 'VesselMaster/vesselMaster.hooks';
import Loading from 'Components/Loading';
import ErrorComponent from 'Components/ErrorComponent';
import { checkValidArray, handleServerResponse, saveEditForm, useSafeFormikContext } from 'GenericForms/Helper';
import ErrorTooltip from 'Components/ErrorTooltip';
import env from 'environment_system/env_system';
import { RootState } from 'index';
import { useSelector, useDispatch } from 'react-redux';
import { getCurrentReportAction } from 'Store/Generic/ReportingSlice';
import { useEditReport } from 'VesselReportingInfo/EditCompletedReports/EditReportProvider';

interface SludgeBilgeWaterComponentType {
    toggleAddTab: any,
    toggleDynamicTabs: any,
    setErrorMessage: (value: boolean) => void,
    editReportBool: boolean,
    activeAddTab: number,
}

const SludgeBilgeWaterComponent = ({
    toggleAddTab,
    toggleDynamicTabs,
    setErrorMessage,
    editReportBool,
    activeAddTab,
}: SludgeBilgeWaterComponentType) => {
    /** State variables start */
    const { previousEnggTabIndex, discardwarning } = useEditReport();
    const { VesselID, VoyageID, ReportID, CurrentReport, reportingLoading, reportingError } = useSelector((state: RootState) => state.Reporting);
    const sludgeWaterDetailsCurrentReport = CurrentReport?.data?.chief_engineer?.lfonds_sludge_water_detail ?? null;
    const bilgeWaterDetailsCurrentReport = CurrentReport?.data?.chief_engineer?.lfonds_bilge_water_detail ?? null;
    const dispatch = useDispatch();
    const [refreshKey, setRefreshKey] = useState(0);
    const formikContext = useSafeFormikContext<any>();
    const editValues = formikContext?.values;
    const setEditValues = formikContext?.setValues;
    const editInitialValues = formikContext?.initialValues;
    const editSubmit = formikContext?.submitForm;
    const editIsSubmitting = formikContext?.isSubmitting;
    /** state variables end */

    /** Queries */
    /** Sludge Tanks */
    const { data: SludgeTanks, isLoading: SludgeTanksLoading, isError: SludgeTanksError } = useQuery(
        [queryKeyes.masters.Sludge.key],
        async () => {
            return await Get(queryKeyes.masters.Sludge.url());
        }, { staleTime: Infinity }
    );
    /** Bilge  Tanks */
    const { data: BilgeTanks, isLoading: BilgeTanksLoading, isError: BilgeTanksError } = useQuery(
        [queryKeyes.masters.Bilge.key],
        async () => {
            return await Get(queryKeyes.masters.Bilge.url());
        }, { staleTime: Infinity }
    );
    /** Queries end */

    /** Assign initial values to Sludge Details' formik object */
    const getInitialSludgeValues = () => {
        let obj: any[] = [];
        if (checkValidArray(editInitialValues?.chief_engineer?.lfonds_sludge_water_detail)) {
            return editInitialValues?.chief_engineer?.lfonds_sludge_water_detail;
        } else if (checkValidArray(sludgeWaterDetailsCurrentReport)) {
            return sludgeWaterDetailsCurrentReport;
        } else {
            if (checkValidArray(SludgeTanks)) {
                SludgeTanks.map((tank: any) => {
                    obj.push({
                        tankname: tank.tank_name,
                        daily_production: 0,
                        total_quantity: 0,
                        tank_number: tank.id,
                        current_filling_ratio: 0,
                        remark: '',
                        vessel: VesselID,
                        voyage_information: VoyageID,
                        vessel_reporting_information: ReportID
                    })
                    return tank;
                })
            }
            return obj;
        }
    }
    /** Assign initial values to Bilge Details' formik object */
    const getInitialBilgeValues = () => {
        let obj: any[] = [];
        if (checkValidArray(editInitialValues?.chief_engineer?.lfonds_bilge_water_detail)) {
            return editInitialValues?.chief_engineer?.lfonds_bilge_water_detail;
        } else if (checkValidArray(bilgeWaterDetailsCurrentReport)) {
            return bilgeWaterDetailsCurrentReport;
        } else {
            if (checkValidArray(BilgeTanks)) {
                BilgeTanks.map((tank: any) => {
                    obj.push({
                        tankname: tank.tank_name,
                        daily_production: 0,
                        total_quantity: 0,
                        tank_number: tank.id,
                        current_filling_ratio: 0,
                        remark: '',
                        vessel: VesselID,
                        voyage_information: VoyageID,
                        vessel_reporting_information: ReportID
                    })
                    return tank
                })
            }
            return obj;
        }
    }

    /** Sludge or Bilge's Formik Object */
    const SludgeBilgeDetailsFormik: any = useFormik({
        enableReinitialize: true,
        initialValues: {
            sludge: getInitialSludgeValues(),
            bilge: getInitialBilgeValues(),
        },
        validationSchema: Yup.object().shape({
            sludge: Yup.array(
                Yup.object({
                    daily_production: Yup.string().matches(/^\d{0,5}(?:\.\d{1,4})?$/, `${commonValidationMessages.before5after4}`),
                    total_quantity: Yup.string().matches(/^\d{0,5}(?:\.\d{1,4})?$/, `${commonValidationMessages.before5after4}`),
                    tanks: Yup.array(
                        Yup.object({
                            tank_number: Yup.string().matches(/^([A-Za-z0-9\- ]){1,50}$/, `${SludgeBilgeWaterMessages.tank_number}`)
                        }),
                    ),
                    remark: Yup.string().max(200, `${commonValidationMessages.max200}`),
                    current_filling_ratio: Yup.string().matches(/^\d{0,5}(?:\.\d{1,4})?$/, `${commonValidationMessages.before5after4}`)
                }),
            ),
            bilge: Yup.array(
                Yup.object({
                    daily_production: Yup.string().matches(/^\d{0,5}(?:\.\d{1,4})?$/, `${commonValidationMessages.before5after4}`),
                    total_quantity: Yup.string().matches(/^\d{0,5}(?:\.\d{1,4})?$/, `${commonValidationMessages.before5after4}`),
                    tanks: Yup.array(
                        Yup.object({
                            tank_number: Yup.string().matches(/^([A-Za-z0-9\- ]){1,50}$/, `${SludgeBilgeWaterMessages.tank_number}`)
                        }),
                    ),
                    remark: Yup.string().max(200, `${commonValidationMessages.max200}`),
                    current_filling_ratio: Yup.string().matches(/^\d{0,5}(?:\.\d{1,4})?$/, `${commonValidationMessages.before5after4}`)
                })
            )
        }),
        onSubmit: async (values, actions) => {
            if (env?.form_validation === false) {
                setErrorMessage(null)
                toggleAddTab(2);
            }
            actions.setSubmitting(true);
            let responseArray: any[] = [];
            if (checkValidArray(sludgeWaterDetailsCurrentReport)) {
                values.sludge.forEach((sludge: any) => {
                    responseArray.push(apiGlobal.put(`/lfonds_sludge_water_details/${sludge.id}/`, sludge));
                })
            } else {
                responseArray.push(apiGlobal.post(`/lfonds_sludge_water_details/`, values.sludge));
            }
            if (checkValidArray(bilgeWaterDetailsCurrentReport)) {
                values.bilge.forEach((bilge: any) => {
                    responseArray.push(apiGlobal.put(`/lfonds_bilge_water_details/${bilge.id}/`, bilge));
                })
            } else {
                responseArray.push(apiGlobal.post(`/lfonds_bilge_water_details/`, values.bilge));
            }
            await handleServerResponse(responseArray).then(async (res: any) => {
                if (res === true) {
                    dispatch(getCurrentReportAction(VesselID, ReportID));
                    setRefreshKey(refreshKey + 1);
                    if (env?.form_validation === true) {
                        setErrorMessage(null)
                        toggleAddTab(2);
                    }
                } else {
                    setErrorMessage(res)
                }
                actions.setSubmitting(false);
            })
        }
    })

    /** useEffect */
    useEffect(() => {
        SludgeBilgeDetailsFormik.initialValues.sludge = getInitialSludgeValues();
        SludgeBilgeDetailsFormik.initialValues.bilge = getInitialBilgeValues();
        setRefreshKey(refreshKey + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [SludgeTanks, BilgeTanks, sludgeWaterDetailsCurrentReport, bilgeWaterDetailsCurrentReport])
    useEffect(() => {
        if (editValues?.chief_engineer) {
            setEditValues({
                ...editValues,
                chief_engineer: {
                    ...editValues?.chief_engineer,
                    lfonds_sludge_water_detail: SludgeBilgeDetailsFormik?.values?.sludge,
                    lfonds_bilge_water_detail: SludgeBilgeDetailsFormik?.values?.bilge,
                }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [SludgeBilgeDetailsFormik?.values])
    /** useEffect end */

    return (
        <Card className='mb-0 border-0'>
            <CardHeader className='p-2 pb-0'>
                <div className="text-center mb-2">
                    <Row>
                        <Col>
                            <h4 className="page_title pos-start mb-0">Sludge & Bilge Water Details</h4>
                            <p className="card-title-desc pos-start mb-0 ">All readings since last noon</p>
                        </Col>
                    </Row>
                </div>
            </CardHeader>
            {(SludgeTanksLoading || BilgeTanksLoading || reportingLoading) && <Loading message='Loading required data!' />}
            {(SludgeTanksError || BilgeTanksError || reportingError) && <ErrorComponent message='Unable to load required data!' />}
            {!(SludgeTanksLoading || BilgeTanksLoading || reportingLoading) &&
                !(SludgeTanksError || BilgeTanksError || reportingError) &&
                <Form autoComplete="off" onSubmit={SludgeBilgeDetailsFormik?.handleSubmit} noValidate key={refreshKey}>
                    <Card className='p-0 mb-0 border-0'>
                        <CardHeader className='p-2 border-bottom-0'>
                            <h5>Sludge Water</h5>
                        </CardHeader>
                        <CardBody className='px-2 py-0'>
                            <div className="table-responsive">
                                <table className="table mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th className='sr-no-width p-2 align-middle'>#</th>
                                            <th className='p-2 align-middle'>Tank name/number</th>
                                            <th style={{ width: '12%' }} className='text-center p-2 align-middle'>Daily production</th>
                                            <th style={{ width: '12%' }} className='text-center p-2 align-middle'>Total Quantity</th>
                                            <th style={{ width: '13%' }} className='text-center p-2 align-middle'>Current filling ratio</th>
                                            <th className='text-center p-2'>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {SludgeBilgeDetailsFormik?.values && SludgeBilgeDetailsFormik?.values?.sludge?.map((tank: any, index: number) => {
                                            return (
                                                <tr key={index}>
                                                    <td className='align-middle text-center p-2'>
                                                        {index + 1}
                                                    </td>
                                                    <td className='align-middle p-2'>
                                                        {tank.tankname}
                                                    </td>
                                                    <td className='align-middle text-center p-2'>
                                                        <div className='d-inline-block'>
                                                            <Input
                                                                onFocus={(e) => e.target.select()}
                                                                type="text"
                                                                id="sludge_daily_production"
                                                                name={`sludge.${index}.daily_production`}
                                                                className="form-control text-right max-width-7"
                                                                onBlur={(e: any) => SludgeBilgeDetailsFormik?.handleChange(e)}
                                                                defaultValue={SludgeBilgeDetailsFormik?.values?.sludge[index]?.daily_production}
                                                            />
                                                        </div>
                                                        {SludgeBilgeDetailsFormik?.errors.sludge && SludgeBilgeDetailsFormik?.errors?.sludge[index]?.daily_production && env?.form_validation && !discardwarning &&
                                                            <ErrorTooltip
                                                                target={`sludge_daily_production`}
                                                                message={SludgeBilgeDetailsFormik?.errors?.sludge[index]?.daily_production}
                                                                open={(SludgeBilgeDetailsFormik?.errors.sludge && SludgeBilgeDetailsFormik?.errors?.sludge[index]?.daily_production) ? true : false}
                                                            />
                                                        }
                                                    </td>
                                                    <td className='align-middle text-center p-2'>
                                                        <div className='d-inline-block'>
                                                            <Input
                                                                onFocus={(e) => e.target.select()}
                                                                type="text"
                                                                id="sludge_total_quantity"
                                                                name={`sludge.${index}.total_quantity`}
                                                                className="form-control text-right max-width-7"
                                                                onBlur={(e: any) => SludgeBilgeDetailsFormik?.handleChange(e)}
                                                                defaultValue={SludgeBilgeDetailsFormik?.values?.sludge[index]?.total_quantity}
                                                            />
                                                        </div>
                                                        {SludgeBilgeDetailsFormik?.errors.sludge && SludgeBilgeDetailsFormik?.errors?.sludge[index]?.total_quantity && env?.form_validation === true && !discardwarning &&
                                                            <ErrorTooltip
                                                                target={`sludge_total_quantity`}
                                                                message={SludgeBilgeDetailsFormik?.errors?.sludge[index]?.total_quantity}
                                                                open={(SludgeBilgeDetailsFormik?.errors.sludge && SludgeBilgeDetailsFormik?.errors?.sludge[index]?.total_quantity) ? true : false}
                                                            />
                                                        }
                                                    </td>
                                                    <td className='align-middle text-center p-2'>
                                                        <div className='d-inline-block'>
                                                            <Input
                                                                onFocus={(e) => e.target.select()}
                                                                type="text"
                                                                id="current_filling_ratio"
                                                                name={`sludge.${index}.current_filling_ratio`}
                                                                className="form-control text-right max-width-7"
                                                                onBlur={(e: any) => SludgeBilgeDetailsFormik?.handleChange(e)}
                                                                defaultValue={SludgeBilgeDetailsFormik?.values?.sludge[index]?.current_filling_ratio}
                                                            />
                                                        </div>
                                                        {SludgeBilgeDetailsFormik?.errors.sludge && SludgeBilgeDetailsFormik?.errors?.sludge[index]?.current_filling_ratio && env?.form_validation === true && !discardwarning && !discardwarning &&
                                                            <ErrorTooltip
                                                                target={`current_filling_ratio`}
                                                                message={SludgeBilgeDetailsFormik?.errors?.sludge[index]?.current_filling_ratio}
                                                                open={(SludgeBilgeDetailsFormik?.errors.sludge && SludgeBilgeDetailsFormik?.errors?.sludge[index]?.current_filling_ratio) ? true : false}
                                                            />
                                                        }
                                                    </td>
                                                    <td className='p-2'>
                                                        <Input
                                                            onFocus={(e) => e.target.select()}
                                                            type="text"
                                                            id="remark"
                                                            name={`sludge.${index}.remark`}
                                                            className="form-control"
                                                            onBlur={(e: any) => SludgeBilgeDetailsFormik?.handleChange(e)}
                                                            defaultValue={SludgeBilgeDetailsFormik?.values?.sludge[index]?.remark}
                                                        />
                                                        {SludgeBilgeDetailsFormik?.errors.sludge && SludgeBilgeDetailsFormik?.errors?.sludge[index]?.remark && env?.form_validation === true && !discardwarning &&
                                                            <ErrorTooltip
                                                                target={`remark`}
                                                                message={SludgeBilgeDetailsFormik?.errors?.sludge[index]?.remark}
                                                                open={(SludgeBilgeDetailsFormik?.errors.sludge && SludgeBilgeDetailsFormik?.errors?.sludge[index]?.remark) ? true : false}
                                                            />
                                                        }
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </CardBody>
                    </Card>
                    <Card className='p-0 mb-0 border-0'>
                        <CardHeader className='p-2 border-bottom-0'>
                            <h5 className='m-0'>Bilge Water</h5>
                        </CardHeader>
                        <CardBody className='px-2 py-0'>
                            <div className="table-responsive">
                                <table className="table mb-2">
                                    <thead className="table-light">
                                        <tr>
                                            <th className='p-2 sr-no-width align-middle'>#</th>
                                            <th className='p-2'>Tank name/number</th>
                                            <th style={{ width: '12%' }} className='text-center align-middle p-2'>Daily production</th>
                                            <th style={{ width: '12%' }} className='text-center align-middle p-2'>Total Quantity</th>
                                            <th style={{ width: '13%' }} className='text-center align-middle p-2'>Current filling ratio</th>
                                            <th className='text-center p-2'>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {SludgeBilgeDetailsFormik?.values && SludgeBilgeDetailsFormik?.values?.bilge?.map((tank: any, index: number) => {
                                            return (
                                                <tr key={index}>
                                                    <td className='align-middle text-center p-2'>
                                                        {index + 1}
                                                    </td>
                                                    <td className='align-middle p-2'>
                                                        {tank.tankname}
                                                    </td>
                                                    <td className='align-middle text-center p-2'>
                                                        <div className='d-inline-block'>
                                                            <Input
                                                                onFocus={(e) => e.target.select()}
                                                                type="text"
                                                                id="daily_production"
                                                                name={`bilge.${index}.daily_production`}
                                                                className="form-control text-right max-width-7"
                                                                onBlur={(e: any) => SludgeBilgeDetailsFormik?.handleChange(e)}
                                                                defaultValue={SludgeBilgeDetailsFormik?.values?.bilge[index]?.daily_production}
                                                            />
                                                        </div>
                                                        {SludgeBilgeDetailsFormik?.errors.bilge && SludgeBilgeDetailsFormik?.errors?.bilge[index]?.daily_production && env?.form_validation === true && !discardwarning &&
                                                            <ErrorTooltip
                                                                target={`daily_production`}
                                                                message={SludgeBilgeDetailsFormik?.errors?.bilge[index]?.daily_production}
                                                                open={(SludgeBilgeDetailsFormik?.errors.bilge && SludgeBilgeDetailsFormik?.errors?.bilge[index]?.daily_production) ? true : false}
                                                            />
                                                        }
                                                    </td>
                                                    <td className='align-middle text-center p-2'>
                                                        <div className='d-inline-block'>
                                                            <Input
                                                                onFocus={(e) => e.target.select()}
                                                                type="text"
                                                                id="total_quantity"
                                                                name={`bilge.${index}.total_quantity`}
                                                                className="form-control text-right max-width-7"
                                                                onBlur={(e: any) => SludgeBilgeDetailsFormik?.handleChange(e)}
                                                                defaultValue={SludgeBilgeDetailsFormik?.values?.bilge[index]?.total_quantity}
                                                            />
                                                        </div>
                                                        {SludgeBilgeDetailsFormik?.errors.bilge && SludgeBilgeDetailsFormik?.errors?.bilge[index]?.total_quantity && env?.form_validation === true && !discardwarning &&
                                                            <ErrorTooltip
                                                                target={`total_quantity`}
                                                                message={SludgeBilgeDetailsFormik?.errors?.bilge[index]?.total_quantity}
                                                                open={(SludgeBilgeDetailsFormik?.errors.bilge && SludgeBilgeDetailsFormik?.errors?.bilge[index]?.total_quantity) ? true : false}
                                                            />
                                                        }
                                                    </td>
                                                    <td className='align-middle text-center p-2'>
                                                        <div className='d-inline-block'>
                                                            <Input
                                                                onFocus={(e) => e.target.select()}
                                                                type="text"
                                                                id="current_filling_ratio"
                                                                name={`bilge.${index}.current_filling_ratio`}
                                                                className="form-control text-right max-width-7"
                                                                onBlur={(e: any) => SludgeBilgeDetailsFormik?.handleChange(e)}
                                                                defaultValue={SludgeBilgeDetailsFormik?.values?.bilge[index]?.current_filling_ratio}
                                                            />
                                                        </div>
                                                        {SludgeBilgeDetailsFormik?.errors.bilge && SludgeBilgeDetailsFormik?.errors?.bilge[index]?.current_filling_ratio && env?.form_validation === true && !discardwarning &&
                                                            <ErrorTooltip
                                                                target={`current_filling_ratio`}
                                                                message={SludgeBilgeDetailsFormik?.errors?.bilge[index]?.current_filling_ratio}
                                                                open={(SludgeBilgeDetailsFormik?.errors.bilge && SludgeBilgeDetailsFormik?.errors?.bilge[index]?.current_filling_ratio) ? true : false}
                                                            />
                                                        }
                                                    </td>
                                                    <td className='p-2'>
                                                        <Input
                                                            onFocus={(e) => e.target.select()}
                                                            type="text"
                                                            id="remark"
                                                            name={`bilge.${index}.remark`}
                                                            className="form-control"
                                                            onBlur={(e: any) => SludgeBilgeDetailsFormik?.handleChange(e)}
                                                            defaultValue={SludgeBilgeDetailsFormik?.values?.bilge[index]?.remark}
                                                        />
                                                        {SludgeBilgeDetailsFormik?.errors.bilge && SludgeBilgeDetailsFormik?.errors?.bilge[index]?.remark && env?.form_validation === true && !discardwarning &&
                                                            <ErrorTooltip
                                                                target={`remark`}
                                                                message={SludgeBilgeDetailsFormik?.errors?.bilge[index]?.remark}
                                                                open={(SludgeBilgeDetailsFormik?.errors.bilge && SludgeBilgeDetailsFormik?.errors?.bilge[index]?.remark) ? true : false}
                                                            />
                                                        }
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </CardBody>
                    </Card>
                    <CardFooter className="p-2 py-3">
                        <Row className="d-flex align-items-center flex-row-reverse">
                            <Col lg={10}>
                                <div className="d-flex flex-wrap gap-3 justify-content-end ">
                                    {/* {editReportBool && (
                                        <Button
                                            type="reset"
                                            color="danger"
                                            className="btn_size_cstm"
                                            onClick={() => {
                                                setEditValues({
                                                    ...editValues,
                                                    chief_engineer: {
                                                        ...editValues?.chief_engineer,
                                                        lfonds_sludge_water_detail: editInitialValues?.chief_engineer?.lfonds_sludge_water_detail,
                                                        lfonds_bilge_water_detail: editInitialValues?.chief_engineer?.lfonds_bilge_water_detail,
                                                    }
                                                })
                                            }}
                                        >
                                            Reset
                                        </Button>
                                    )} */}
                                    <Button
                                        type={editReportBool ? "button" : "submit"}
                                        color="primary"
                                        className="btn_size_cstm"
                                        disabled={SludgeBilgeDetailsFormik?.isSubmitting || editIsSubmitting}
                                        onClick={async () => {
                                            const validationErrors = await SludgeBilgeDetailsFormik?.validateForm();
                                            Object.keys(SludgeBilgeDetailsFormik?.values || {}).forEach((key: any) => {
                                                SludgeBilgeDetailsFormik?.setFieldTouched(key, true);
                                            });
                                            if (editReportBool && Object.keys(validationErrors).length === 0) {
                                                await saveEditForm(editSubmit, toggleAddTab, activeAddTab);
                                            }
                                        }}
                                    >
                                        {(SludgeBilgeDetailsFormik?.isSubmitting || editIsSubmitting) && (
                                            <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                                        )}
                                        Next <i className="bx bx-chevron-right ms-1" />
                                    </Button>
                                </div>
                            </Col>
                            <Col lg={2}>
                                <Button
                                    type="button"
                                    color="primary"
                                    className="btn_size_cstm"
                                    onClick={() => {
                                        SludgeBilgeDetailsFormik?.setErrors({});
                                        setErrorMessage(null);
                                        toggleDynamicTabs(previousEnggTabIndex - 1);
                                    }}
                                >
                                    <i className="bx bx-chevron-left me-1" /> Previous
                                </Button>
                            </Col>
                        </Row>
                    </CardFooter>
                    <FormValuesDebug values={[SludgeBilgeDetailsFormik?.values, SludgeBilgeDetailsFormik?.errors, SludgeBilgeDetailsFormik.initialValues]} />
                </Form>
            }
        </Card>
    )
}

export default SludgeBilgeWaterComponent